import { useEffect, useRef, useState } from "react";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isEqual } from "lodash"

import { AgGridReact } from "ag-grid-react";
import { FormControl, Box, Chip, TextField, Autocomplete, Typography, IconButton, InputAdornment, Tooltip, Backdrop, CircularProgress } from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import MoveDownIcon from '@mui/icons-material/MoveDown';
// import PostAddIcon from '@mui/icons-material/PostAdd';
import { red } from "@mui/material/colors";
import planVersionService from "@plannel/services/data/plan-version-service";
import appConfigService from "@plannel/services/system/app-config-service";
import userService from "@plannel/services/system/user-service";
import demandValService from "@plannel/services/data/demand-val-service";

import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader"
import Dialog from "@plannel/components/Dialog"
import Snackbar from "@plannel/components/Snackbar";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { SettingsButton, DeleteButton } from "@plannel/components/ActionIconButton";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";

import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import axiosUtil from "@plannel/utils/axios-bigint";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { FS } from "@plannel/utils/font-util";

const { DP } = CodeUtil.module;
const { SCENARIO, READY, RELEASED, DELETE, UNPLANNED, PLANNED, FAILED, RUNNING, RELEASING } = CodeUtil.versionStatusCode;
const VERSION_EXP_PERIOD = "VERSION_EXP_PERIOD";
const DP_ARCHIVE_VERSION = "DP_ARCHIVE_VERSION";
const DP_ARCHIVE_ACTUAL_DATE = "DP_ARCHIVE_ACTUAL_DATE";

const { snackBarMessageColor } = ColorUtil;
const VersionMgmt = ({ viewName, t, title }) => {
  const userInfo = axiosUtil.jsonbigParse(localStorage.getItem("user"));

  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [userRoles, setUserRoles] = useState(reduxViewState?.userRoles ?? []);
  const [modules, setModules] = useState([]);
  const statuses = [SCENARIO, RELEASED, DELETE, UNPLANNED, PLANNED, FAILED, RUNNING];
  const [module, setModule] = useState(reduxViewState?.module ?? null);
  const [status, setStatus] = useState(reduxViewState?.status ?? null);
  const [isDisable, setIsDisable] = useState(false);
  const [expireDays, setExpireDays] = useState("");
  const versionExpDaysConfig = useRef({});

  const [basicVersions, setBasicVersions] = useState([]);
  const [deleteVersions, setDeleteVersions] = useState([]);
  const [isDelete, setIsDelete] = useState(false);

  const verColsHide = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "verColsHide") ? reduxViewState.verColsHide : true;
  const deleteVerColsHide = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "deleteVerColsHide") ? reduxViewState.deleteVerColsHide : true;

  const basicGridRef = useRef();
  const deleteGridRef = useRef();

  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [progressOn, setProgressOn] = useState(false);

  const [archiveDate, setArchiveDate] = useState(null);
  const [archiveVersion, setArchiveVersion] = useState("");
  const archiveVersionConfig = useRef([]);

  const getRequestParams = () => {
    const params = {};

    if (module) {
      params.moduleCd = module;
    }

    if (status) {
      params.statusCds = [status, DELETE];
    }

    return params;
  }

  const setExpireDayByVersion = (data, days) => {
    const version = { ...data };

    const currentDate = dateUtils.getDateWithoutTime(new ZDate());
    const expireDate = dateUtils.getDateWithoutTime(new ZDate(version.updatedTs));
    expireDate.setDate(expireDate.getDate() + Number(days));

    const diffTime = expireDate - currentDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const expireDateStr = dateUtils.formatDate(expireDate, "yyyy-MM-dd");

    version.expDate = expireDateStr;
    version.expDays = diffDays;

    return version;
  }

  const setVersions = (results, days) => {
    const versions = [];
    const delVersions = [];

    for (const result of results) {
      const { moduleCd, statusCd } = result;
      if (modules.includes(moduleCd)) {
        if (isEqual(moduleCd, DP) && isEqual(statusCd, READY)) {
          continue;
        }

        if (isEqual(statusCd, DELETE)) {
          const version = setExpireDayByVersion(result, days);
          delVersions.push(version);
        } else {
          versions.push(result);
        }
      }
    }

    setBasicVersions(versions);
    setDeleteVersions(delVersions.reverse());
  }

  const retrievePlanVersion = days => {
    const params = getRequestParams();
    planVersionService.getVersionForMaster(params)
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setVersions(results, days);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { module, status, userRoles });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      });
  }

  const restorePlanVersion = planVersions => {
    if (isEmpty(planVersions)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
      return;
    }

    planVersionService.restoreVersion(planVersions)
      .then(() => {
        retrievePlanVersion(expireDays);
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_RestoreFailed") });
      });
  }

  const deletePlanVersion = planVersions => {
    const ids = planVersions.map(planVersion => planVersion.id);
    if (isEmpty(ids)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
      return;
    }
    setSnackInfo({open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_TaskCompleteAlert")});
    setIsDelete(true);
    planVersionService.deleteVersionWithData(ids)
      .then(() => {
        retrievePlanVersion(expireDays);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        setIsDelete(false);
      })
      .catch(e => {
        debug(e);
        if (e?.status === 409) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_taskAlreadyRunning") });
          return;
        }
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        setIsDelete(false);
      });
  }

  const progressArchive = () => {
    setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_ArchiveStart") });
    demandValService.archive()
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_ArchiveSuccess") });
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ArchiveFailed") });
      });
  }

  const makeObjVal = (selectedVersions) => {
    setProgressOn(true);
    const ids = selectedVersions.map(planVersion => planVersion.id);
    const params = { versionIds: ids }
    demandValService.makeObjVal(params)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DataSuccess") });
        setProgressOn(false);
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DataFailed") });
        setProgressOn(false);
      });
  }

  const handleDialog = (action, _falseAction, el) => {
    if (isEqual(action, "RESTORE")) {
      const selectedVersions = deleteGridRef.current.api.getSelectedRows();
      restorePlanVersion(selectedVersions);
    } else if (isEqual(action, "DELETE")) {
      const { gridApi } = el;
      const selectedVersions = gridApi.current.api.getSelectedRows();
      deletePlanVersion(selectedVersions);
    } else if (isEqual(action, "ARCHIVE")) {
      progressArchive();
    } else if (isEqual(action, "MAKEOBJVAL")) {
      const selectedVersions = basicGridRef.current.api.getSelectedRows();
      if (!selectedVersions || isEmpty(selectedVersions)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData,  content: t("MSG_NoSelectedModuleVersion", { module: t("dp") })});
        return;
      }
      let hasDp = false;
      const dpVersions = [];
      for (const version of selectedVersions) {
        if (isEqual(version.moduleCd, DP) && isEqual(version.statusCd, RELEASED)) {
          dpVersions.push(version);
          hasDp = true;
        }
      }
      if (!hasDp) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData,  content: t("MSG_NoSelectedModuleVersion", { module: t("dp") })});
        return;
      }
      makeObjVal(dpVersions);
    }
  }

  const retrieveExpireDays = (versionLoad = false) => {
    const codes = [VERSION_EXP_PERIOD];
    appConfigService.getAppConfigs({ configCds: codes })
      .then(response => {
        const { data } = response;

        if (isEmpty(data)) {
          debug("No default expiration date data. Check the database.");
        } else {
          const versionConfig = data.find(config => isEqual(config.configCd, VERSION_EXP_PERIOD));
          const days = Number(versionConfig.numVal);
          setExpireDays(days);
          versionExpDaysConfig.current = versionConfig;

          if (versionLoad) {
            retrievePlanVersion(days);
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveArchiveSetting = () => {
    const codes = [DP_ARCHIVE_VERSION, DP_ARCHIVE_ACTUAL_DATE];
    appConfigService.getAppConfigs({ configCds: codes })
      .then(response => {
        const { data } = response;
        if (isEmpty(data)) {
          debug("No default expiration date data. Check the database.");
        } else {
          for (const result of data) {
            if (result?.configCd === DP_ARCHIVE_VERSION) {
              setArchiveVersion(result?.numVal);
            } else if (result?.configCd === DP_ARCHIVE_ACTUAL_DATE) {
              setArchiveDate(result?.dateVal);
            }
          }
          archiveVersionConfig.current = data;
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: userInfo.username })
      .then(
        (response) => {
          const userAccess = response.data;
          setUserRoles(userAccess?.roles);
        },
      )
      .catch(e => { debug(e); });
  };


  const checkValidation = numVal => {
    return isNaN(numVal) || isEqual(numVal, "") || !Number.isInteger(Number(numVal));
  }

  const saveVersionExpireDays = () => {
    if (checkValidation(expireDays)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterNumber") });
      return;
    }

    const saveData = { ...versionExpDaysConfig.current, numVal: expireDays };
    appConfigService.upsertAppConfig([saveData])
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        setModalOpen(false);
        retrievePlanVersion(expireDays);
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      });
  }

  const makeParams = () => {
    const params = [];
    //기존 저장된 데이터가 없을 시
    if (isEmpty(archiveVersionConfig.current)) {
      if (archiveDate) {
        const param = { moduleCd: DP, activeFlg: true, configCd: DP_ARCHIVE_ACTUAL_DATE, dateVal: dateUtils.formatDate(archiveDate, "yyyy-MM-dd")};
        params.push(param);
      }
      if (archiveVersion) {
        const param = { moduleCd: DP, activeFlg: true, configCd: DP_ARCHIVE_VERSION, numVal: archiveVersion };
        params.push(param);
      }
    } else {
      for (const config of archiveVersionConfig.current) {
        if (isEqual(config?.configCd, DP_ARCHIVE_ACTUAL_DATE)) {
          if (archiveDate) {
            const param = { ...config, dateVal: dateUtils.formatDate(archiveDate, "yyyy-MM-dd")};
            params.push(param);
          }
        } else if (isEqual(config?.configCd, DP_ARCHIVE_VERSION)) {
          if (archiveVersion) {
            const param = { ...config, numVal: archiveVersion };
            params.push(param);
          }
        }
      }
    }
    return params;
  }

  const saveArchiveSetting = () => {
    if (checkValidation(archiveVersion)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterNumber") });
      return;
    }
    if (archiveVersion < 1) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntGreaterThanValue", { N: 1 }) });
      return;
    }
    if (archiveDate === null || archiveDate === undefined) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_PleaseEnterValues", { values: t("archiveDate") }) });
      return;
    }

    if (dateUtils.formatDate(archiveDate, 'yyyy-MM-dd') >= dateUtils.formatDate(new ZDate(), 'yyyy-MM-dd')) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ConfirmArchiveDate") });
      return;
    }
    appConfigService.upsertAppConfig(makeParams())
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        setArchiveModalOpen(false);
        retrieveArchiveSetting();
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      });
  }

  const handleClose = action => {
    if (isEqual(action, "cancel")) {
      setModalOpen(false);
    } else if (isEqual(action, "save")) {
      saveVersionExpireDays();
    }
  }

  const setModulesByAppRole = () => {
    const appRolePrefix = "ROLE_APP_";
    const availableModules = [];
    if (!isEmpty(userRoles)) {
      const appRoles = userRoles.filter(role => role.includes("APP"));
      if (appRoles) {
        for (const appRole of appRoles) {
          availableModules.push(appRole.replace(appRolePrefix, ""));
        }
      }
    }
    setModules(availableModules);
  }

  const onSelectionChanged = () => {
    const selectedVersions = basicGridRef?.current?.api?.getSelectedRows();
    if (!selectedVersions || isEmpty(selectedVersions)) {
      setIsDisable(false);
      return;
    }
    const running = selectedVersions.some(version => version.statusCd === RUNNING || version.statusCd === RELEASING);
    setIsDisable(running);
  }

  useEffect(() => {
    if (isDelete) {
      window.dispatchEvent(new CustomEvent('export-started'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDelete]);

  useEffect(() => {
    if (!isEmpty(modules)) {
      if (checkValidation(expireDays)) {
        retrieveExpireDays(true);
      } else {
        retrievePlanVersion(expireDays);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules, module, status]);

  useEffect(() => {
    if (modalOpen) {
      retrieveExpireDays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    if (!isEmpty(userRoles)) {
      setModulesByAppRole();
      retrieveArchiveSetting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles])

  useEffect(() => {
    if (isEmpty(userRoles)) {
      retrieveUserRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 계획 버전 상태 → 채움-틴트 칩 (버전 드롭다운/분류 모달 배지와 동일 규격)
  const statusCellRenderer = (params) => {
    const statusCd = params.value;
    if (statusCd == null || statusCd === "") return statusCd ?? "";
    const label = CodeUtil.versionStatusLabel(statusCd);
    return <Chip size="small" label={label} sx={{ height: 22, fontSize: FS.BODY, ...ColorUtil.tintChipSx(ColorUtil.versionStatusColor(statusCd)) }} />;
  };

  const basicVersionColumnDefs = [
    {
      headerName: "versionName", field: "name", type: ["nonEditableColumn"], width: 300
    },
    { headerName: "module", field: "moduleCd", type: ["nonEditableColumn"], width: 150 },
    { headerName: "status", field: "statusCd", type: ["nonEditableColumn"], width: 200, cellRenderer: statusCellRenderer },
    { headerName: "descTxt", field: "descTxt", type: ["nonEditableColumn"], width: 400, flex: 1, minWidth: 300 },
    { headerName: "parentVerName", field: "parentName", type: ["nonEditableColumn"], width: 200 },
    { headerName: "refVerName", field: "refVerName", type: ["nonEditableColumn"], width: 200 },
    {
      headerName: "execTs", field: "execTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 200,
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: verColsHide, reduxKey: "verColsHide",
        detailColumns: ["releaseTs", "createdTs", "createdByName", "updatedTs", "updatedByName"]
      },
    },
    { headerName: "released", field: "releaseTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 200, hide: verColsHide },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 200, hide: verColsHide },
    { headerName: "createdBy", field: "createdByName", type: "nonEditableColumn", width: 150, hide: verColsHide },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 200, hide: verColsHide },
    { headerName: "updatedBy", field: "updatedByName", type: "nonEditableColumn", width: 150, hide: verColsHide }
  ];

  const deleteVersionColumnDefs = [
    {
      headerName: "versionName", field: "name", type: ["nonEditableColumn"], width: 300
    },
    { headerName: "module", field: "moduleCd", type: ["nonEditableColumn"], width: 200 },
    { headerName: "descTxt", field: "descTxt", type: ["nonEditableColumn"], width: 400, flex: 1, minWidth: 300 },
    { headerName: "parentVerName", field: "parentName", type: ["nonEditableColumn"], width: 200 },
    { headerName: "refVerName", field: "refVerName", type: ["nonEditableColumn"], width: 200 },
    {
      headerName: "expDate", field: "expDate", type: "nonEditableColumn", width: 200,
      cellStyle: params => {
        const { data } = params;

        if (Object.prototype.hasOwnProperty.call(data, "expDays") && data.expDays < 7) {
          return { color: red[500], fontWeight: "bold" };
        }

        return {};
      }
    },
    {
      headerName: "execTs", field: "execTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 200,
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: deleteVerColsHide, reduxKey: "deleteVerColsHide",
        detailColumns: ["releaseTs", "createdTs", "createdByName", "updatedTs", "updatedByName"]
      },
    },
    { headerName: "released", field: "releaseTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 200, hide: deleteVerColsHide },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 200, hide: deleteVerColsHide },
    { headerName: "createdBy", field: "createdByName", type: "nonEditableColumn", width: 150, hide: deleteVerColsHide },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 200, hide: deleteVerColsHide },
    { headerName: "updatedBy", field: "updatedByName", type: "nonEditableColumn", width: 150, hide: deleteVerColsHide }
  ];

  const inputPropsByValue = value => {
    return value ? { endAdornment: <InputAdornment position="end" sx={{ mr: 1 }}>{t("days")}</InputAdornment> } : {};
  }

  const inputPropsByVersionValue = value => {
    return value ? { endAdornment: <InputAdornment position="end" sx={{ mr: 1 }}>{t("versions")}</InputAdornment> } : {};
  }

  return (
    <>
      <Box sx={{ display: "flex", overflow: "auto", width: "100%" }}>
        <Autocomplete
          options={modules}
          value={module}
          onChange={(_, newValue) => setModule(newValue)}
          size="small"
          sx={{ my: 1, minWidth: 190 }}
          renderInput={(params) => (
            <TextField {...params} label={t("module")} />
          )}
        />
        <Autocomplete
          options={statuses}
          value={status}
          onChange={(_, newValue) => setStatus(newValue)}
          size="small"
          sx={{ my: 1, ml: 1, minWidth: 250 }}
          renderInput={(params) => (
            <TextField {...params} label={t("status")} />
          )}
        />
        <Box sx={{ mt: 1.5, ml: "auto" }}>
          <DeleteButton disabled={isDisable} onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeletePermanentlyConfirm"), el: { gridApi: basicGridRef } })} />
          {/*<Tooltip title={t("makeObjVal")}>*/}
          {/*  <Box component={"span"}>*/}
          {/*    <IconButton aria-label={"makeObjVal"} color="black" size="small" onClick={() => setDialogInfo({ open: true, title: "makeObjVal", content: t("MSG_MakeObjVal")})}>*/}
          {/*      <PostAddIcon />*/}
          {/*    </IconButton>*/}
          {/*  </Box>*/}
          {/*</Tooltip>*/}
          <Tooltip title={t("NOTI_Archive")}>
            <Box component={"span"}>
              {
                archiveVersion && archiveDate ?
                  <IconButton aria-label={"archive"} color="black" size="small" onClick={() => setDialogInfo({ open: true, title: "archive", content: `${t("MSG_ArchiveWithNumber", { number: archiveVersion, date: archiveDate })}\n${t("MSG_ArchiveDetail")}`,})}>
                    <MoveDownIcon />
                  </IconButton>
                    :
                  <IconButton aria-label={"archive"} color="black" size="small" onClick={() => setDialogInfo({ open: true, title: "archive", content: `${t("MSG_ArchiveSetting")}\n${t("MSG_ArchiveDetail")}`})}>
                    <MoveDownIcon />
                  </IconButton>
                  }
            </Box>
          </Tooltip>
          <SettingsButton onClick={() => setArchiveModalOpen(true)} />
        </Box>
      </Box>
      <Box id={"version-mgmt"} style={{ height: "calc(100vh - 150px)" }}>
        <Box id={"version-grid"} className="ag-theme-balham" mb={0.5} style={{ height: "calc(47.5vh - 95px)" }}>
          <AgGridReact
            ref={basicGridRef}
            columnDefs={GridUtils.getColumnDefs(basicVersionColumnDefs)}
            rowData={basicVersions}
            {...DefaultGridSetting({ title, viewName, columnDefs: basicVersionColumnDefs, gridId: "basicGrid" })}
            rowHeight={34}
            onGridReady={params => GridUtils.setColumnDefs({ ...params, columnDefs: basicVersionColumnDefs, viewName, gridId: "basicGrid", initState: true })}
            rowSelection={{ mode: 'multiRow', enableClickSelection: false, checkboxes: true, headerCheckbox: true, selectAll: 'filtered' }}
            onSelectionChanged={onSelectionChanged}
          />
        </Box>
        <Box sx={{ display: "flex", overflow: "auto", width: "100%", mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, alignSelf: "flex-end" }}>
            {t("restorableVersions")}
          </Typography>
          <Box sx={{ ml: "auto" }}>
            <Tooltip title={t("restore")}>
              <Box component={"span"}>
                <IconButton aria-label="restore" color="info" size="small" onClick={() => setDialogInfo({ open: true, title: "restore", content: t("MSG_VersionRestore") })}>
                  <RestoreIcon />
                </IconButton>
              </Box>
            </Tooltip>
            <DeleteButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeletePermanentlyConfirm"), el: { gridApi: deleteGridRef } })} />
            <SettingsButton onClick={() => setModalOpen(true)} />
          </Box>
        </Box>
        <Box id={"delete-version-grid"} className="ag-theme-balham" mb={1} style={{ height: "calc(47.5vh - 50px)" }}>
          <AgGridReact
            ref={deleteGridRef}
            columnDefs={GridUtils.getColumnDefs(deleteVersionColumnDefs)}
            rowData={deleteVersions}
            {...DefaultGridSetting({ title, viewName, columnDefs: deleteVersionColumnDefs, gridId: "deleteGrid" })}
            onGridReady={params => GridUtils.setColumnDefs({ ...params, columnDefs: deleteVersionColumnDefs, viewName, gridId: "deleteGrid", initState: true })}
            rowSelection={{ mode: 'multiRow', enableClickSelection: false, checkboxes: true, headerCheckbox: true, selectAll: 'filtered' }}
          />
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        actionName={dialogInfo.actionName}
        content={dialogInfo.content}
        onHandler={handleDialog}
        el={dialogInfo.el}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
      {/*
        * 제목이 md(360)를 넘긴다 — vi "Đặt thời gian hết hạn của phiên bản"(35자 ≈ 368px) + 패딩 40px.
        */}
      <BaseConfigModal
        open={modalOpen}
        onClose={() => handleClose("cancel")}
        title={t("setVersionExpiredPeriod")}
        onSave={() => handleClose("save")}
        size="lg">
        <Typography variant="body2" sx={{ mb: 2 }} gutterBottom>{t("MSG_VersionAutoDelete")}</Typography>
        <TextField
          size="small"
          name="expireDays"
          label={t("expPeriod")}
          value={expireDays}
          sx={{ maxWidth: 200 }}
          required
          error={checkValidation(expireDays)}
          InputProps={inputPropsByValue(expireDays)}
          onChange={e => setExpireDays(e.target.value)}
        />
      </BaseConfigModal>
      {/*
        * onClose 가 handleClose("cancel") 이었는데 그 함수는 setModalOpen(false) — **Expiration 모달**을 닫는다.
        * 즉 이 모달은 ESC·배경클릭으로 안 닫혔다(Cancel 버튼만 setArchiveModalOpen(false) 로 동작). 같이 고친다.
        */}
      <BaseConfigModal
        open={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        title={t("setArchiveSetting")}
        onSave={saveArchiveSetting}
        size="md">
        <Typography variant="body2" sx={{ mb: 2 }} gutterBottom>{t("MSG_Archive")}</Typography>
        <Typography variant="body2" sx={{ mb: 1 }} gutterBottom>{t("archiveDate")}</Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <FormControl size="small" sx={{ display: "flex", mb: 2 }}>
            <DatePicker
              label={t("keepDate")}
              format={"yyyy-MM-dd"}
              views={["year", "month", "day"] }
              size="small"
              sx={{ height: "40px", maxWidth: 200 }}
              value={archiveDate}
              onAccept={newValue => {
                setArchiveDate(newValue);
              }}
              slotProps={{ textField: { size: "small" } }}
            />
          </FormControl>
        </LocalizationProvider>
        <Typography variant="body2" sx={{ mb: 1 }} gutterBottom>{t("archiveVersion")}</Typography>
        <TextField
          size="small"
          name="archiveVersion"
          label={t("archiveVersionCount")}
          value={archiveVersion}
          sx={{ maxWidth: 200 }}
          error={checkValidation(archiveVersion)}
          InputProps={inputPropsByVersionValue(archiveVersion)}
          onChange={e => {
            const value = e.target.value;
            // 숫자 또는 빈 문자열만 허용 (음수 기호 제거)
            if (/^-?\d*$/.test(value) && !value.startsWith('-')) {
            setArchiveVersion(value);
            }
          }}
        />
      </BaseConfigModal>
    </>
  )
};

export default withTranslation()(VersionMgmt);
